/**
 * The shell's data hooks (spec M0-F4 §5, §7.8).
 *
 * `shell.bootstrap` is the only aggregate procedure in the product and it exists so the frame
 * paints in one round trip. Everything else the shell needs is a separate, cacheable query.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC, useTRPCClient } from '../../lib/trpc';
import { onAccountChanged } from '../../lib/realtime/socket';
import { useDemoShell } from './demo';
import { configurePreferenceTransport, hydratePreferences } from './preferences';

export interface ShellCapabilities {
  workspaceSwitcher: boolean;
  workspaceTree: boolean;
  sharedWithMeOnly: boolean;
  canCreateBoard: boolean;
  canCreateFolder: boolean;
  canCreateWorkspace: boolean;
  canCreateDashboard: boolean;
  favorites: boolean;
  myWork: boolean;
  inbox: boolean;
  notifications: boolean;
  search: 'account' | 'scoped';
  inviteMembers: boolean;
  administration: boolean;
  developersPage: boolean;
  canCreateApiToken: boolean;
  teams: boolean;
  trash: boolean;
  archive: boolean;
  importData: boolean;
}

/**
 * What the shell renders while `shell.bootstrap` is loading or has failed (E12): the chrome
 * paints, every capability is off, and the retry lives on the sidebar.
 */
export const CAPABILITIES_WHILE_LOADING: ShellCapabilities = {
  workspaceSwitcher: false,
  workspaceTree: false,
  sharedWithMeOnly: false,
  canCreateBoard: false,
  canCreateFolder: false,
  canCreateWorkspace: false,
  canCreateDashboard: false,
  favorites: false,
  myWork: false,
  inbox: false,
  notifications: false,
  search: 'scoped',
  inviteMembers: false,
  administration: false,
  developersPage: false,
  canCreateApiToken: false,
  teams: false,
  trash: false,
  archive: false,
  importData: false,
};

/**
 * **P-160 — the preference store's write path.**
 *
 * The store's remote write is a single injected function and this is where the tRPC client and
 * the shell's lifetime meet, so this is where it is installed. `preferences.set` is per key
 * (F4 §4.1: one `user_preferences` row per key), already debounced by the store, and the UI never
 * waits on it: a failed write leaves the optimistic value on screen and the `localStorage`
 * mirror behind it, which is what the shell did before the server was in the loop at all.
 *
 * The demo shell (`/__ui`) has no session, so it keeps the local-only store.
 *
 * It is a hook of its own — and its cleanup only removes **its own** transport — because
 * `useBootstrap` has more than one caller and the first one to unmount used to unwire the rest.
 */
export function usePreferenceTransport(): void {
  const client = useTRPCClient();
  const demo = useDemoShell();
  useEffect(() => {
    if (demo) return undefined;
    return configurePreferenceTransport((key, value) => {
      void client.preferences.set.mutate({ key, value }).catch(() => {
        // Reported by the mutation cache when a write goes through React Query; here the store's
        // optimistic value and its mirror are the fallback, so a lost write costs a reload.
      });
    });
  }, [client, demo]);
}

export function useBootstrap() {
  const trpc = useTRPC();
  const demo = useDemoShell();
  const queryClient = useQueryClient();
  const options = trpc.shell.bootstrap.queryOptions(undefined, {
    // The shell is the first thing on screen; a failed boot must not spin forever (E12).
    retry: 1,
    staleTime: 60_000,
  });
  const query = useQuery(
    demo
      ? // The demo shell answers with fixture data; the shape is the procedure's own.
        ({
          ...options,
          queryKey: ['demo', 'shell.bootstrap'],
          queryFn: () => Promise.resolve(demo.bootstrap),
        } as unknown as typeof options)
      : options,
  );

  /**
   * **P-160.** The preference store's write path is a single injected function, and this is the
   * one place in the app where the tRPC client and the shell's lifetime meet — so this is where
   * it is installed. `preferences.set` is per key (F4 §4.1: one `user_preferences` row per key),
   * already debounced by the store, and the UI never waits on it: a failed write leaves the
   * optimistic value on screen and the `localStorage` mirror behind it, which is what the shell
   * did before the server was in the loop at all.
   *
   * The demo shell (`/__ui`) has no session, so it keeps the local-only store.
   */
  usePreferenceTransport();

  /**
   * **P-240 / M8-P2 D1** — *"changing the brand colour re-paints the top bar and the primary
   * button without a reload, in a second browser tab, within 1 s."*
   *
   * The api published `permissions.changed` on the control channel, which is server-side only:
   * it drops caches and re-runs the room guards and no browser ever hears it. `account.changed`
   * is fanned out to every connected socket, and this is what turns that frame into a re-read —
   * of `shell.bootstrap`, with the reader's own rights, because the frame carries no data.
   *
   * It covers the product name, the wordmark, the logo and the capability matrix too, all of
   * which had the identical problem.
   */
  useEffect(() => {
    if (demo) return undefined;
    return onAccountChanged(() => {
      void queryClient.invalidateQueries({ queryKey: options.queryKey });
    });
    // `options.queryKey` is stable for this procedure and `demo` never changes at runtime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demo, queryClient]);

  // Preferences are read once and then owned by their own store, so every consumer reads a
  // synchronous value instead of a query result that can be `undefined` on the first frame.
  useEffect(() => {
    if (query.data) hydratePreferences(query.data.preferences);
  }, [query.data]);

  return query;
}

export function useSidebarData(workspaceId: string | null) {
  const trpc = useTRPC();
  const demo = useDemoShell();
  const options = trpc.shell.sidebar.queryOptions({ workspaceId: workspaceId ?? '0' });
  return useQuery(
    demo
      ? ({
          ...options,
          queryKey: ['demo', 'shell.sidebar'],
          queryFn: () => Promise.resolve(demo.sidebar),
          staleTime: Number.POSITIVE_INFINITY,
        } as unknown as typeof options)
      : { ...options, enabled: workspaceId !== null, staleTime: 30_000 },
  );
}

export function useWorkspaceList(query: string) {
  const trpc = useTRPC();
  const demo = useDemoShell();
  // Under 50 workspaces the switcher filters client-side; above it, the query round-trips
  // (W2). `limit` is the switch: the server always answers with `total`.
  const options = trpc.shell.workspaces.queryOptions({ limit: 50, ...(query ? { query } : {}) });
  return useQuery(
    demo
      ? ({
          ...options,
          queryKey: ['demo', 'shell.workspaces', query],
          queryFn: () =>
            Promise.resolve({
              recent: demo.bootstrap.workspaces.recent,
              all: demo.bootstrap.workspaces.recent,
              total: demo.bootstrap.workspaces.total,
              defaultId: demo.bootstrap.workspaces.defaultId,
            }),
          staleTime: Number.POSITIVE_INFINITY,
        } as unknown as typeof options)
      : { ...options, staleTime: 30_000 },
  );
}

export function useBoardSummary(boardId: string | null) {
  const trpc = useTRPC();
  const demo = useDemoShell();
  const options = trpc.shell.board.queryOptions({ boardId: boardId ?? '0' });
  return useQuery(
    demo
      ? ({
          ...options,
          queryKey: ['demo', 'shell.board', boardId],
          queryFn: () => Promise.resolve(demo.board),
          staleTime: Number.POSITIVE_INFINITY,
        } as unknown as typeof options)
      : { ...options, enabled: boardId !== null, retry: false, staleTime: 30_000 },
  );
}
